/**
 * The campaign page's palette and the class strings it repeats.
 *
 * The charity product styles itself directly rather than through the product
 * design system, so the ground, card, ink and rule colours are defined once
 * here and referenced everywhere else.
 */

/** Page ground. */
export const CAMPAIGN_GROUND_HEX = "#131315";

/** Raised surface: bib rows, chips. */
export const CAMPAIGN_CARD_HEX = "#1C1C1F";

/** Bib white, the page's primary ink. */
export const CAMPAIGN_INK_HEX = "#F6F4F0";

/** Bone: the guaranteed stretch of the tracker, already covered. */
export const CAMPAIGN_BONE_HEX = "#DDD8CF";

/** Brand orange: everything earned past the guarantee. */
export const CAMPAIGN_ORANGE_HEX = "#E66E1A";

/** Secondary ink. */
export const CAMPAIGN_MUTED_HEX = "#A2A09C";

/** Hairline rule between sections. */
export const CAMPAIGN_HAIRLINE_HEX = "#2C2C30";

/** Ink for an action that takes something away. Clears AA on the page ground. */
export const CAMPAIGN_DANGER_HEX = "#E5534B";

/** Secondary ink as a class. */
export const CAMPAIGN_MUTED_TEXT_CLASS = "text-[#A2A09C]";

/**
 * The type a section heading is set in, without the space beneath it.
 *
 * Split out so a heading that carries its own controls — one that opens and
 * closes, say — sets its own spacing while still reading at the same step as
 * every other section heading on the surface.
 */
export const CAMPAIGN_SECTION_HEADING_TYPE_CLASS = "text-[22px] font-extrabold tracking-[-0.02em] text-[#F6F4F0]";

/**
 * Small caps heading that opens each section below the tally.
 *
 * Carries its own step to the content beneath it, so a section cannot set a
 * different one and no two headings sit differently on the page.
 */
export const CAMPAIGN_SECTION_HEADING_CLASS = `${CAMPAIGN_SECTION_HEADING_TYPE_CLASS} m-0 mb-5`;

/** A block inside a section: smaller than the section it belongs to, louder than its prose. */
export const CAMPAIGN_SUBHEADING_CLASS =
  "text-[16px] font-bold tracking-[-0.01em] text-[#F6F4F0] m-0 mb-3";

/**
 * The small print that belongs to a section, set beneath it.
 *
 * Kept visibly quieter than the section's own copy so a caveat reads as a
 * footnote rather than as another thing to read.
 */
export const CAMPAIGN_SECTION_NOTE_CLASS = "text-[12px] leading-relaxed text-[#7C7A76]";

/**
 * The column every top-level block of the page sits in.
 *
 * One `gap` sets the whole page's vertical rhythm and the column's own padding
 * runs on the same step, so no block carries a margin of its own and the
 * spacing cannot drift as blocks are added, removed or reordered.
 *
 * The page has one ladder, widest first: this step between top-level blocks,
 * `CAMPAIGN_SECTION_BLOCK_CLASS` wherever a new block begins inside a section,
 * the heading classes' own step down to what they head, and
 * `CAMPAIGN_SECTION_ACTION_CLASS` before the quiet action that closes a
 * section. A block that needs a gap takes one of those rather than a number.
 */
export const CAMPAIGN_PAGE_BLOCK_STACK_CLASS = "flex flex-col gap-12";

/** The column itself: the block stack, padded on the same step. */
export const CAMPAIGN_PAGE_COLUMN_CLASS = `${CAMPAIGN_PAGE_BLOCK_STACK_CLASS} py-12`;

/**
 * Hairline-topped section block.
 *
 * The rule belongs to the section it opens, so it sits nearer that section than
 * the one it closes: the column gap above it, one block step below. Centring it
 * instead made every ruled break twice the size of an unruled one, which read
 * as two different rhythms on one page.
 */
export const CAMPAIGN_SECTION_CLASS = "border-t border-[#2C2C30] pt-8";

/**
 * Where a new block begins inside a section — a second panel, a sub-heading and
 * its content, a list under a paragraph.
 */
export const CAMPAIGN_SECTION_BLOCK_CLASS = "mt-8";

/**
 * The step between a section's own content and the quiet action that closes it.
 */
export const CAMPAIGN_SECTION_ACTION_CLASS = "mt-5";

/**
 * The focus treatment every interactive thing on the charity surface carries.
 *
 * Exported separately from the four action styles so a control that is none of
 * them — a field, a checkbox, a native picker — still focuses the same way.
 * Keyboard focus that changes shape from one control to the next reads as a
 * different site, not as a different control.
 */
export const CAMPAIGN_FOCUS_RING_CLASS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E66E1A]";

/**
 * The dominant action: the one thing a page is asking for.
 *
 * This and the three styles below are the only action styles the charity
 * surface has. Anything new picks one of them rather than inventing a fifth.
 * They differ by rank and nothing else: weight steps down extrabold, semibold,
 * normal; padding steps down 24/12, 16/10, 12/6; the radius is `rounded-[5px]`
 * throughout; and every one of them carries `CAMPAIGN_FOCUS_RING_CLASS`.
 *
 * Written to survive being applied to the shared `Button` component, whose own
 * height and shadow it has to overrule on the pages that use one.
 *
 * Every hover state names both its ground and its ink from the palette above.
 * Naming only one of the two leaves the other to be supplied by whatever else
 * is on the element — the shared `Button` variants carry their own
 * `hover:text-*`, and the app-wide dark theme rewrites Tailwind's named
 * `white`/`gray` classes to their dark-mode counterparts — so a half-specified
 * hover lands on a ground and an ink that were never chosen together.
 */
export const CAMPAIGN_PRIMARY_ACTION_CLASS = `inline-flex h-auto items-center justify-center gap-2 rounded-[5px] bg-[#F6F4F0] px-6 py-3 text-[14.5px] font-extrabold tracking-[-0.01em] text-[#131315] shadow-none transition-colors hover:bg-[#DDD8CF] hover:text-[#131315] motion-reduce:transition-none ${CAMPAIGN_FOCUS_RING_CLASS}`;

/**
 * The way onward from a section, and any action that is not the primary one.
 *
 * Ghost rather than solid and a size down from the primary, so a page can carry
 * several without any of them competing with the one action it is asking for.
 */
export const CAMPAIGN_SECONDARY_ACTION_CLASS = `inline-flex h-auto items-center justify-center gap-2 rounded-[5px] border border-[#2C2C30] bg-transparent px-4 py-2.5 text-[13.5px] font-semibold text-[#F6F4F0] shadow-none transition-colors hover:border-[#A2A09C] hover:bg-[#1C1C1F] hover:text-[#F6F4F0] motion-reduce:transition-none ${CAMPAIGN_FOCUS_RING_CLASS}`;

/**
 * A small utility control that operates the thing next to it rather than moving
 * the reader on: pause, next, a filter.
 */
export const CAMPAIGN_QUIET_CONTROL_CLASS = `inline-flex h-auto items-center gap-2 rounded-[5px] border border-[#2C2C30] bg-transparent px-3 py-1.5 text-[11px] font-normal uppercase tracking-[0.09em] text-[#A2A09C] shadow-none transition-colors hover:border-[#A2A09C] hover:bg-[#1C1C1F] hover:text-[#F6F4F0] motion-reduce:transition-none ${CAMPAIGN_FOCUS_RING_CLASS}`;

/**
 * A control that takes something away and cannot be casually undone: removing a
 * household member, withdrawing a report.
 *
 * Sized as a quiet control because it sits in a row beside ordinary ones, but
 * it must never be mistaken for one - a reader scanning a list of members
 * should be able to tell which control ends something before they read the
 * label. Colour is not the only signal at rest, since the border and hover
 * ground move together with it.
 */
export const CAMPAIGN_DESTRUCTIVE_ACTION_CLASS = `inline-flex h-auto items-center gap-2 rounded-[5px] border border-[#A8463D] bg-transparent px-3 py-1.5 text-[11px] font-normal uppercase tracking-[0.09em] text-[#E5534B] shadow-none transition-colors hover:border-[#E5534B] hover:bg-[#2A1614] hover:text-[#F2837B] motion-reduce:transition-none ${CAMPAIGN_FOCUS_RING_CLASS}`;

/**
 * A link set in running text or in a row of them: the footer, a mailto on a
 * label.
 *
 * Underlined rather than distinguished by colour alone, because these sit in
 * muted grey among muted grey text that is not a link. Carries no type size —
 * a quiet link takes the size of whatever it is set in, from footer copy down
 * to a tick label on the tracker.
 */
export const CAMPAIGN_QUIET_LINK_CLASS = `rounded-sm text-[#A2A09C] underline decoration-dotted underline-offset-[3px] transition-colors hover:text-[#F6F4F0] motion-reduce:transition-none ${CAMPAIGN_FOCUS_RING_CLASS}`;

/**
 * The one measure every run of prose on the page is set to.
 *
 * Prose runs the full column. Blocks previously ran at 52ch, 56ch and the full
 * container width, so some paragraphs stopped short while their neighbours ran
 * on — ragged rather than deliberate. Kept as a single lever so the widths
 * cannot drift apart again; change it here and every block follows.
 */
export const CAMPAIGN_PROSE_CLASS = "max-w-none";

/** The white-on-dark number a household is given at join. */
export const CAMPAIGN_BIB_BADGE_CLASS =
  "min-w-[38px] rounded-[3px] bg-[#F6F4F0] px-2 py-[3px] text-center text-[13px] font-extrabold tabular-nums text-[#131315]";
